import { Observable, BehaviorSubject, ReplaySubject } from 'rxjs';
import { map, mapTo, pairwise } from 'rxjs/operators';
import { ApiConfigManager } from '@sg/api/base-api-lib';
import { UsersApi, UsersApiClient } from '@sg/api/users-api';
import { KillOnDestroy } from '@sg/utils';
import { UserSessionService } from '../services/user-session.service';

/**
 * Base class for session based apps with a multi-entity setup.
 * Multi-entity setup: A single logged in user with multiple child entities where each entity
 * acts acts like a logical user, having it's own data.
 */
export abstract class SgEntityBasedSession {
  ready: Promise<boolean>;
  user: UsersApi.UserDto;

  user$: Observable<UsersApi.UserDto>;
  entities$: Observable<Array<UsersApi.LegalEntityBaseDto>>;
  currentBaseEntity$: Observable<UsersApi.LegalEntityBaseDto>;
  currentEntity$: Observable<UsersApi.LegalEntityDto>;
  isSlTech$: Observable<boolean>;

  get entities(): Array<UsersApi.LegalEntityBaseDto> {
    return this._entities$.value;
  }
  get currentBaseEntity(): UsersApi.LegalEntityBaseDto {
    return this._currentBaseEntity$.value;
  }
  get currentEntity(): UsersApi.LegalEntityDto {
    return this._currentEntity$.value;
  }

  protected _user$: ReplaySubject<UsersApi.UserDto>;
  protected _entities$: BehaviorSubject<Array<UsersApi.LegalEntityBaseDto>>;
  protected _currentBaseEntity$: BehaviorSubject<UsersApi.LegalEntityBaseDto>;
  protected _currentEntity$: BehaviorSubject<UsersApi.LegalEntityDto>;

  constructor(
    protected readonly entityIdKey: keyof UsersApi.LegalEntityBaseDto,
    protected cfg: ApiConfigManager,
    protected userSession: UserSessionService,
    public usersApi: UsersApiClient
  ) {
    this._user$ = new ReplaySubject<UsersApi.UserDto>(1);
    this.user$ = this._user$.asObservable();
    this._entities$ = new BehaviorSubject<Array<UsersApi.LegalEntityBaseDto>>([]);
    this.entities$ = this._entities$.asObservable();
    this._currentBaseEntity$ = new BehaviorSubject<UsersApi.LegalEntityBaseDto>(undefined);
    this.currentBaseEntity$ = this._currentBaseEntity$.asObservable();
    this._currentEntity$ = new BehaviorSubject<UsersApi.LegalEntityDto>(undefined);
    this.currentEntity$ = this._currentEntity$.asObservable();
    this.isSlTech$ = this.currentEntity$.pipe(map((el) => el.isSlTech));

    // we should have a token at this point (right after login, which renew token if required)
    this.cfg.setAuth(userSession.authAdapter.token);
    userSession.events$.pipe(KillOnDestroy(this)).subscribe((event) => {
      if (event.event === 'renew') {
        this.cfg.setAuth(this.userSession.authAdapter.token);
      }
    });

    this.currentEntity$.pipe(KillOnDestroy(this)).subscribe((entity) => {
      this.cfg.applyAll((cfg) => cfg.setDefaultHeader('X-SG-USER', entity ? entity.id : undefined));
      this.onEntityChanged(entity);
    });
  }

  init(autoFetchFirstEntity = true): Promise<boolean> {
    if (!this.ready) {
      this.ready = this.updateUser()
        .then(() => {
          const entities = this.getEntities();
          if (!Array.isArray(entities) || entities.length === 0) {
            return this.userSession.logout().then(() => false);
          }
          this._entities$.next(entities);
          if (autoFetchFirstEntity && entities && entities.length > 0) {
            const appName = `${this.userSession.env.appName}-selected-le`;
            const cachedLegalEntityId = localStorage.getItem(appName);
            let legalEntityId: string;
            if (cachedLegalEntityId) {
              const matchedEntity = entities.find((entity) => entity[this.entityIdKey] === cachedLegalEntityId);
              legalEntityId = matchedEntity ? matchedEntity[this.entityIdKey] : entities[0][this.entityIdKey];
            } else {
              legalEntityId = entities[0][this.entityIdKey];
            }
            return this.changeEntity(legalEntityId).then(() => true);
          } else {
            return true;
          }
        })
        .catch((err) => {
          // A rejection here (e.g. a permission gap on the entity-fetch call) must not leave `ready`
          // permanently pending — every caller awaits it (route guards, app-root bootstrap), so an
          // unhandled rejection hangs navigation/loading indefinitely instead of surfacing an error state.
          console.error('SgEntityBasedSession.init failed', err);
          return false;
        });
    }
    return this.ready;
  }

  updateUser(): Promise<UsersApi.UserDto> {
    return this.getUser().then((user) => {
      this.user = user;
      this._user$.next(user);
      return user;
    });
  }

  changeEntity(id: string | UsersApi.LegalEntityBaseDto): Promise<UsersApi.LegalEntityDto> {
    const idKey = this.entityIdKey;
    const entity = typeof id === 'string' ? this.getEntities().find((ent) => ent[idKey] === id) : id;
    const appName = `${this.userSession.env.appName}-selected-le`;
    localStorage.setItem(appName, entity.id);
    this._currentBaseEntity$.next(entity);
    return this.getEntity(entity.id).then((entity) => {
      this._currentEntity$.next(this.getEntityWithFormatAdvisorName(entity));
      return entity;
    });
  }

  refreshEntity(): Promise<UsersApi.LegalEntityDto> {
    if (!this.currentEntity) {
      return Promise.reject('No current entity');
    }
    return this.getEntity(this.currentEntity.id).then((entity) => {
      this._currentEntity$.next(this.getEntityWithFormatAdvisorName(entity));
      return entity;
    });
  }

  onEntitySwap(killOnDestroyToken?: any): Observable<void> {
    return this.currentEntity$.pipe(pairwise(), mapTo(undefined), KillOnDestroy(killOnDestroyToken || this));
  }

  protected getEntities(): UsersApi.LegalEntityBaseDto[] {
    return this.user.legalEntities;
  }

  protected getUser(): Promise<UsersApi.UserDto> {
    const appName = this.userSession.env.appName.replace('sg-', '');
    return this.usersApi.getUsersUserInfo({ app: appName as any, activeOnly: true });
  }

  protected getUserObs(): Observable<UsersApi.UserDto> {
    const appName = this.userSession.env.appName.replace('sg-', '');
    return this.usersApi.getUsersUserInfo({ app: appName as any, activeOnly: true }, { observe: 'body' });
  }

  protected getEntity(id: string): Promise<UsersApi.LegalEntityDto> {
    return this.usersApi.getUsersLegalEntityInfo(id);
  }

  protected abstract onEntityChanged(entity: UsersApi.LegalEntityDto): void;

  /** The names of advisor users are auto generated and contain GUID, so we replace them with "Advisor"  */
  private getEntityWithFormatAdvisorName(legalEntity: UsersApi.LegalEntityDto): UsersApi.LegalEntityDto {
    if (!legalEntity.displayName?.toLowerCase().startsWith('advisor-')) {
      return legalEntity;
    }

    return { ...legalEntity, displayName: 'Advisor' };
  }
}
